// Tier 2 teams migration, Phase C+D: replaces the teams portion of App.jsx's watchDoc mechanism,
// which used to be:
//   watchDoc("teams", setTeams, d=>dedupeIds(d.teams||d.items||SEED.teams))
// and subscribed to the single warriors/teams document, reading its `teams` array field. This
// module instead subscribes to the new top-level `teams` collection (one document per team,
// doc ID = the team's existing id — see scripts/migrate-teams.cjs) and reconstructs an equivalent
// in-memory array. warriors/teams.teams is now a frozen historical snapshot; nothing writes to it.
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';

// Converts one teams/{id} document into the shape the app's in-memory `teams` array has always
// used. `nodeId` USED to be dropped here too ("nothing client-side reads it yet") — that stopped
// being true once the shared node-filter component (Roster View, Participation Tracker, Stats,
// Roster Builder pool) needed to filter the client's own `teams` state by nodeId. Same fix, same
// reasoning as playersDocToRecord (src/utils/playersCollection.js) — checked the one write site
// that spreads a team object (saveTeam, src/App.jsx) and confirmed it builds its write payload from
// explicitly named fields, never a whole-object spread, so carrying nodeId through here doesn't
// leak it into any write that shouldn't have it.
// Unlike players, team `id` is already a string in the source data (assigned via uid() in
// saveTeam — confirmed against live production data in Phase A, not assumed), so there's no
// numeric conversion to undo here — the doc ID and the `id` field already match as-is.
export function teamsDocToRecord(docId, data) {
  return { ...data, id: docId };
}

// Live-subscription replacement for watchDoc("teams", setTeams, ...) — same ergonomics (takes the
// setter, returns an unsubscribe function) so Phase C+D can drop this in place of the existing
// watchDoc call with minimal disruption to the surrounding effect.
export function watchTeamsCollection(setter) {
  return onSnapshot(collection(db, 'teams'), (snap) => {
    setter(snap.docs.map((d) => teamsDocToRecord(d.id, d.data())));
  });
}

// Node-scoped variant (teams pilot, mirroring watchPlayersCollectionForNodes) — constrained via
// where('nodeId','in',nodeIds) to a caller-supplied permitted-node list. Used for the admin role
// only; coach/parent keep the unscoped watchTeamsCollection above, since neither has a node concept
// yet and the teams/{teamId} security rule's isCoach()/isParent() read branches stay unscoped to
// match. Mirrors the rule's admin branch exactly — the rule is what actually enforces this, this is
// just what makes the query itself succeed instead of being rejected as unprovable. Same 30-value
// 'in' operator ceiling applies as documented in watchPlayersCollectionForNodes.
export function watchTeamsCollectionForNodes(nodeIds, setter) {
  return onSnapshot(query(collection(db, 'teams'), where('nodeId', 'in', nodeIds)), (snap) => {
    setter(snap.docs.map((d) => teamsDocToRecord(d.id, d.data())));
  });
}
